import React from "react";
import { ExternalLink, Disc } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const ALBUMS = [
  {
    cover:
      "https://static.wixstatic.com/media/952455_d1572f8812454cb5899bd0ba2a8ef457f002.jpg/v1/fill/w_980,h_551,enc_auto/file.jpeg",
    spotify: "https://open.spotify.com/album/2ESmQ0zCWUTU0nrArWWCkj",
    embed:
      "https://open.spotify.com/embed/album/2ESmQ0zCWUTU0nrArWWCkj",
    label: "Da Vinci Publishing — Osaka, Japan",
  },
  {
    cover:
      "https://static.wixstatic.com/media/952455_1d9b2ddd7fac424ea8081b91c00ff337f002.jpg/v1/fill/w_980,h_551,enc_auto/file.jpeg",
    spotify: "https://open.spotify.com/album/1KtJ452k0b9RxUZHahjpoQ",
    embed:
      "https://open.spotify.com/embed/album/1KtJ452k0b9RxUZHahjpoQ",
    label: "Da Vinci Publishing — Osaka, Japan",
  },
];

export const Discografia = () => {
  const { t } = useLanguage();

  return (
    <section
      id="discography"
      data-testid="discography-section"
      className="relative py-28 lg:py-36 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="overline" data-testid="discography-overline">
              {t.discography.overline}
            </div>
            <h2
              data-testid="discography-title"
              className="font-serif-display font-light mt-5 text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.05]"
            >
              {t.discography.title}
            </h2>
          </div>
          <Disc className="text-[#D4AF37] hidden lg:block" size={42} strokeWidth={1} />
        </div>

        <div className="space-y-24">
          {t.discography.albums.map((album, i) => {
            const meta = ALBUMS[i];
            const reverse = i % 2 === 1;
            return (
              <div
                key={i}
                data-testid={`album-${i}`}
                className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center`}
              >
                <div
                  className={`lg:col-span-5 ${
                    reverse ? "lg:order-2" : ""
                  }`}
                >
                  <div className="relative aspect-square overflow-hidden border border-white/10 group">
                    <img
                      src={meta.cover}
                      alt={album.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 text-[#D4AF37] font-serif-bodoni text-xs tracking-[0.2em]">
                      {String(i + 1).padStart(2, "0")} / {String(t.discography.albums.length).padStart(2, "0")}
                    </div>
                  </div>
                </div>
                <div
                  className={`lg:col-span-7 ${
                    reverse ? "lg:order-1 lg:text-right" : ""
                  }`}
                >
                  <div
                    className={`flex items-baseline gap-4 ${
                      reverse ? "lg:justify-end" : ""
                    }`}
                  >
                    <span className="font-serif-bodoni italic text-[#D4AF37] text-2xl">
                      {album.year}
                    </span>
                    <div className="h-px flex-1 max-w-[80px] bg-[#D4AF37]/40" />
                  </div>
                  <h3
                    data-testid={`album-title-${i}`}
                    className="font-serif-display font-light mt-3 text-3xl lg:text-5xl text-[#FDFBF7] tracking-tight"
                  >
                    {album.title}
                  </h3>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#71717A] mt-3">
                    {meta.label}
                  </p>
                  <p className="mt-6 text-[#A1A1AA] text-base lg:text-lg leading-relaxed font-light max-w-xl lg:inline-block">
                    {album.description}
                  </p>
                  <div className={`mt-8 ${reverse ? "lg:flex lg:justify-end" : ""}`}>
                    <a
                      data-testid={`album-spotify-${i}`}
                      href={meta.spotify}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 border border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[#D4AF37] px-6 py-3 text-xs tracking-[0.25em] uppercase transition-all"
                    >
                      {t.discography.listenSpotify}
                      <ExternalLink size={14} />
                    </a>
                  </div>

                  <a
                    data-testid={`album-spotify-embed-${i}`}
                    href={meta.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 flex items-center gap-5 border border-white/10 hover:border-[#D4AF37]/60 bg-[#0E0E0E] p-5 transition-all group/card"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#1DB954] flex items-center justify-center shrink-0 group-hover/card:scale-105 transition-transform">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="black" aria-hidden="true">
                        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.622.622 0 01-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.622.622 0 11-.277-1.213c3.809-.871 7.077-.496 9.713 1.114a.622.622 0 01.207.856zm1.224-2.722a.778.778 0 01-1.071.257c-2.687-1.652-6.785-2.131-9.965-1.166a.778.778 0 11-.452-1.49c3.632-1.102 8.147-.568 11.231 1.328.366.225.482.703.257 1.071zm.105-2.835c-3.223-1.914-8.54-2.09-11.617-1.156a.934.934 0 11-.542-1.788c3.532-1.072 9.404-.865 13.115 1.338a.934.934 0 11-.956 1.606z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717A]">
                        Spotify
                      </div>
                      <div className="font-serif-display text-lg text-[#FDFBF7] truncate">
                        {album.title}
                      </div>
                    </div>
                    <ExternalLink size={16} className="text-[#D4AF37] shrink-0 group-hover/card:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Discografia;
